import { CSSProperties, ReactNode, RefObject, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface PanelRect {
    top: number;
    left: number;
    width: number;
    openUpward: boolean;
    triggerTop: number;
}

interface DropdownPanelPortalProps {
    triggerRef: RefObject<HTMLElement>;
    isOpen: boolean;
    children: ReactNode;
    className?: string;
    /** Ref that will be set to the portal wrapper div — use this in outside-click handlers */
    panelRef?: RefObject<HTMLDivElement>;
    /** Called after the portal mounts — use this to focus elements inside the panel */
    onMount?: () => void;
    /** Maximum height of the panel in px — used to decide whether to flip upward. Defaults to 240. */
    maxPanelHeight?: number;
    /** Called when the user scrolls while the panel is open — use this to close the dropdown */
    onScrollClose?: () => void;
}

/**
 * Renders dropdown panel content into document.body via a React portal using
 * position:fixed so it is always anchored to the viewport and never causes
 * the page body to scroll.
 *
 * If the panel would overflow the bottom of the viewport, it opens upward
 * above the trigger instead (standard "flip" behaviour).
 *
 * Calls onScrollClose when any scroll container scrolls, so the parent can
 * close the dropdown — keeping the panel aligned with its trigger.
 */
export const DropdownPanelPortal = ({
    triggerRef,
    isOpen,
    children,
    className,
    panelRef,
    onMount,
    maxPanelHeight = 240,
    onScrollClose,
}: DropdownPanelPortalProps) => {
    const [rect, setRect] = useState<PanelRect | null>(null);
    const rafRef = useRef<number>();

    const setRef = (el: HTMLDivElement | null) => {
        if (panelRef) {
            // eslint-disable-next-line no-param-reassign
            (panelRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
        }
        if (el && onMount) {
            onMount();
        }
    };

    const updateRect = () => {
        if (!triggerRef.current) return;
        const r = triggerRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - r.bottom;
        const spaceAbove = r.top;
        const openUpward = spaceBelow < maxPanelHeight && spaceAbove > spaceBelow;
        setRect({
            top: r.bottom,
            left: r.left,
            width: r.width,
            openUpward,
            triggerTop: r.top,
        });
    };

    useEffect(() => {
        if (!isOpen) {
            setRect(null);
            return undefined;
        }

        updateRect();

        const handleScroll = (e: Event) => {
            // Ignore scroll events from within the panel itself (e.g. the dropdown's
            // own scrollbar) — only close when an external container scrolls
            if (
                panelRef &&
                (panelRef as React.MutableRefObject<HTMLDivElement | null>).current?.contains(e.target as Node)
            ) {
                return;
            }
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            rafRef.current = requestAnimationFrame(() => {
                if (onScrollClose) {
                    // Close the dropdown — trigger has moved away from panel
                    onScrollClose();
                } else {
                    // Fallback: reposition
                    updateRect();
                }
            });
        };

        const handleResize = () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            rafRef.current = requestAnimationFrame(updateRect);
        };

        // Capture phase catches scroll on any element including app-main-layout
        window.addEventListener('scroll', handleScroll, true);
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('scroll', handleScroll, true);
            window.removeEventListener('resize', handleResize);
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, [isOpen, onScrollClose]);

    if (!isOpen || !rect) return null;

    const style: CSSProperties = rect.openUpward
        ? {
              position: 'fixed',
              bottom: window.innerHeight - rect.triggerTop,
              left: rect.left,
              width: rect.width,
              zIndex: 9999,
              maxHeight: Math.min(rect.triggerTop, maxPanelHeight),
          }
        : {
              position: 'fixed',
              top: rect.top,
              left: rect.left,
              width: rect.width,
              zIndex: 9999,
              maxHeight: Math.min(window.innerHeight - rect.top, maxPanelHeight),
          };

    return createPortal(
        <div style={style} className={className} ref={setRef}>
            {children}
        </div>,
        document.body
    );
};
